import { type ApiError } from "@/services/intranet-portal-services";
import { type ErrorPayload } from "@/types/errors";
import { toast } from "react-toastify";

let sessionExpiryHandled = false;

const stringifyValidationError = (value: unknown): string => {
  if (typeof value === "string") {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(stringifyValidationError).join(", ");
  }

  if (typeof value === "object" && value !== null) {
    // Handle ValidationError objects safely
    const obj = value as Record<string, unknown>;
    if (typeof obj.message === "string") {
      return obj.message;
    }

    return JSON.stringify(value);
  }

  return String(value);
};

export const isApiError = (error: unknown): error is ApiError => {
  return typeof error === "object" && error !== null && "status" in error && "body" in error;
};

export const isNetworkError = (error: unknown): boolean => {
  if (typeof error !== "object" || error === null) return false;

  // Axios specific network errors
  const err = error as { isAxiosError?: boolean; code?: string; message?: string };
  if (err.isAxiosError === true) {
    const code = err.code ?? "";
    const message = err.message ?? "";
    return (
      code === "ERR_NETWORK" ||
      code === "ECONNABORTED" ||
      message === "Network Error" ||
      message.toLowerCase().includes("timeout")
    );
  }

  return false;
};

export const isSessionExpired = (error: unknown): boolean => {
  if (!isApiError(error)) return false;

  if (error.status === 401) return true;

  const payload: ErrorPayload | undefined = error.body?.error ?? error.body;
  if (!payload) return false;

  const text = `${payload.code ?? ""} ${payload.message ?? ""}`.toLowerCase();

  return ["token", "expired", "session", "invalidtoken", "unauthorized"].some((keyword) =>
    text.includes(keyword)
  );
};

export const handleSessionExpiry = (logout?: () => void): void => {
  if (sessionExpiryHandled) return;

  sessionExpiryHandled = true;

  toast.error("Your session has expired. Please log in again.", {
    toastId: "session-expired",
    autoClose: 3000,
    onClose: logout
  });
};

export const resetSessionExpiryFlag = (): void => {
  sessionExpiryHandled = false;
};

const parseValidationErrors = (validationErrors: unknown): string | null => {
  if (validationErrors == null) return null;

  // Handle string format (legacy/fallback)
  if (typeof validationErrors === "string") {
    return validationErrors;
  }

  // Handle array format: ValidationError[]
  if (Array.isArray(validationErrors)) {
    const errors = validationErrors as Array<{ message?: string; members?: string[] } | string>;
    return errors
      .map((err) => {
        if (typeof err === "string") return err;
        const message = err.message ?? "";
        const members = Array.isArray(err.members) ? err.members.join(", ") : "";

        return members !== "" ? `${message}: ${members}` : message;
      })
      .filter((msg) => msg !== "")
      .join("; ");
  }

  // Handle object format (legacy: { fieldName: messages })
  if (typeof validationErrors === "object") {
    return Object.entries(validationErrors)
      .map(([field, messages]) => {
        const message = stringifyValidationError(messages);
        return `${field}: ${message}`;
      })
      .join("; ");
  }

  return null;
};

const mapErrorCode = (code: string | null | undefined): string | null => {
  if (code == null || code === "") return null;

  const errorCodeMap: Record<string, string> = {
    "User is already inactive.": "Employee is already inactive",
    "User is already active.": "Employee is already active",
    Forbidden: "You don't have permission to perform this action",
    NotFound: "The requested resource was not found",
    "No Project Distinct Membersfound!!": "No distinct project members found for the selected range"
  };

  return errorCodeMap[code] ?? code;
};

const getStatusMessage = (status: number): string => {
  const statusMap: Record<number, string> = {
    400: "Invalid request",
    401: "Session expired",
    403: "You don't have permission to perform this action",
    404: "Resource not found",
    409: "Conflict with existing data",
    429: "Too many requests. Please try again later",
    500: "Server error. Please try again later",
    502: "Bad Gateway",
    503: "Service temporarily unavailable",
    504: "Gateway Timeout"
  };

  return statusMap[status] ?? `Request failed with status ${status}`;
};

export const getApiErrorMessage = (error: unknown): string | null => {
  if (isNetworkError(error)) {
    return "Network error. Please check your connection.";
  }

  if (!isApiError(error)) {
    return "Unexpected error occurred";
  }

  if (isSessionExpired(error)) {
    return null;
  }

  const payload: ErrorPayload | undefined = error.body?.error ?? error.body;
  if (!payload) {
    return getStatusMessage(error.status);
  }

  // 1. Try validation errors
  const validationMessage = parseValidationErrors(payload.validationErrors);
  if (validationMessage !== null && validationMessage !== "") return validationMessage;

  // 2. Try specific error code
  const codeMessage = mapErrorCode(payload.code);
  if (codeMessage !== null && codeMessage !== "") return codeMessage;

  // 3. Try generic payload message (if not default internal error)
  if (
    payload.message != null &&
    payload.message !== "" &&
    payload.message !== "An internal error occurred during your request!"
  ) {
    return payload.message;
  }

  // 4. Final fallback to status code
  return getStatusMessage(error.status);
};

/**
 * Shows a deduplicated toast for API errors.
 *
 * @param error - The error object to process
 * @param logout - Callback to execute if session is expired
 */
export const showApiError = (error: unknown, logout?: () => void): void => {
  if (isSessionExpired(error)) {
    handleSessionExpiry(logout);
    return;
  }

  const message = getApiErrorMessage(error);

  if (message != null && message !== "") {
    // Deduplicate based on the message content
    toast.error(message, {
      toastId: `api-error-${message.replace(/\s+/g, "-").toLowerCase()}`,
      autoClose: 5000
    });
  }
};
